/**
 * Custom hook pour la gestion des politiques de relance (Reminder Policies)
 * ✅ SKILL FACTURXPRO: Architecture 3 couches
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ReminderPolicy,
  reminderPoliciesAPI,
  CreateReminderPolicyDTO,
  UpdateReminderPolicyDTO,
} from '../utils/reminderPoliciesAPI';
import { notify } from '../stores/notificationStore';
import { logger } from '../utils/logger';

interface UseReminderPoliciesManagementReturn {
  // Data
  policies: ReminderPolicy[];
  invoicePolicies: ReminderPolicy[];
  quotePolicies: ReminderPolicy[];

  // UI State
  loading: boolean;
  showForm: boolean;
  editingPolicy: ReminderPolicy | null;

  // Filter State
  entityTypeFilter: 'all' | 'invoice' | 'quote';
  activeOnlyFilter: boolean;

  // Actions - CRUD
  fetchPolicies: () => Promise<void>;
  createPolicy: (data: CreateReminderPolicyDTO) => Promise<void>;
  updatePolicy: (id: string, data: UpdateReminderPolicyDTO) => Promise<void>;
  deletePolicy: (id: string) => Promise<void>;
  toggleActive: (id: string, currentStatus: boolean) => Promise<void>;

  // Actions - UI
  setShowForm: (show: boolean) => void;
  setEditingPolicy: (policy: ReminderPolicy | null) => void;
  setEntityTypeFilter: (type: 'all' | 'invoice' | 'quote') => void;
  setActiveOnlyFilter: (active: boolean) => void;

  // Computed
  filteredPolicies: ReminderPolicy[];
}

export function useReminderPoliciesManagement(): UseReminderPoliciesManagementReturn {
  // ==================== STATE ====================

  // Data state
  const [policies, setPolicies] = useState<ReminderPolicy[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<ReminderPolicy | null>(null);

  // Filter state
  const [entityTypeFilter, setEntityTypeFilter] = useState<'all' | 'invoice' | 'quote'>('all');
  const [activeOnlyFilter, setActiveOnlyFilter] = useState(false);

  // ==================== DATA LOADING ====================

  /**
   * Charger toutes les politiques de relance
   */
  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reminderPoliciesAPI.getAll();
      setPolicies(response.data || []);
    } catch (error) {
      logger.error('fetchPolicies error:', error);
      notify.error('Erreur lors du chargement des politiques de relance', 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== CRUD OPERATIONS ====================

  /**
   * Créer une politique de relance
   */
  const createPolicy = useCallback(
    async (data: CreateReminderPolicyDTO) => {
      try {
        setLoading(true);
        await reminderPoliciesAPI.create(data);
        notify.success('Politique de relance créée avec succès', 'Succès');
        await fetchPolicies();
        setShowForm(false);
      } catch (error) {
        logger.error('createPolicy error:', error);
        notify.error('Erreur lors de la création de la politique de relance', 'Erreur');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchPolicies]
  );

  /**
   * Mettre à jour une politique de relance
   */
  const updatePolicy = useCallback(
    async (id: string, data: UpdateReminderPolicyDTO) => {
      try {
        setLoading(true);
        await reminderPoliciesAPI.update(id, data);
        notify.success('Politique de relance mise à jour avec succès', 'Succès');
        await fetchPolicies();
        setShowForm(false);
        setEditingPolicy(null);
      } catch (error) {
        logger.error('updatePolicy error:', error);
        notify.error('Erreur lors de la mise à jour de la politique de relance', 'Erreur');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchPolicies]
  );

  /**
   * Supprimer une politique de relance
   */
  const deletePolicy = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        await reminderPoliciesAPI.delete(id);
        notify.success('Politique de relance supprimée avec succès', 'Succès');
        await fetchPolicies();
      } catch (error) {
        logger.error('deletePolicy error:', error);
        notify.error('Erreur lors de la suppression de la politique de relance', 'Erreur');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchPolicies]
  );

  /**
   * Activer/Désactiver une politique de relance
   */
  const toggleActive = useCallback(
    async (id: string, currentStatus: boolean) => {
      try {
        setLoading(true);
        await reminderPoliciesAPI.toggleActive(id, !currentStatus);
        notify.success('Statut de la politique modifié', 'Succès');
        await fetchPolicies();
      } catch (error) {
        logger.error('toggleActive error:', error);
        notify.error('Erreur lors de la modification du statut', 'Erreur');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchPolicies]
  );

  // ==================== COMPUTED VALUES ====================

  /**
   * Politiques filtrées selon les critères actifs
   */
  const filteredPolicies = useCallback(() => {
    let result = [...policies];

    // Filtre par type d'entité
    if (entityTypeFilter !== 'all') {
      result = result.filter((p) => p.entityType === entityTypeFilter);
    }

    // Filtre par statut actif
    if (activeOnlyFilter) {
      result = result.filter((p) => p.isActive);
    }

    return result;
  }, [policies, entityTypeFilter, activeOnlyFilter])();

  /**
   * Politiques pour factures uniquement
   */
  const invoicePolicies = policies.filter((p) => p.entityType === 'invoice');

  /**
   * Politiques pour devis uniquement
   */
  const quotePolicies = policies.filter((p) => p.entityType === 'quote');

  // ==================== EFFECTS ====================

  // Charger les politiques au montage
  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // ==================== RETURN ====================

  return {
    // Data
    policies,
    invoicePolicies,
    quotePolicies,

    // UI State
    loading,
    showForm,
    editingPolicy,

    // Filter State
    entityTypeFilter,
    activeOnlyFilter,

    // Actions - CRUD
    fetchPolicies,
    createPolicy,
    updatePolicy,
    deletePolicy,
    toggleActive,

    // Actions - UI
    setShowForm,
    setEditingPolicy,
    setEntityTypeFilter,
    setActiveOnlyFilter,

    // Computed
    filteredPolicies,
  };
}
